import type { XrefPumps, Correlations } from "@/composables/forms/formTypes";
import { PoundsPerCubicFoot } from "@/libs/units/density";
import {
  getNominalPowerBrakeHP,
  getPumpEfficiencyRatio,
  getTemperatureDeltaForPumpStage
} from "@/libs/calculations/pumps";
import type { BasicData } from "@/libs/calculations/fluidProperties/d";
import { getFluidPropertiesContext } from "../../fluidProperties/getFluidPropertiesContext";
import type { FluidPropertiesContext } from "../../fluidProperties/d";
import type { LagrangeInterpolationMapWithGroupings } from "@/composables/scenarioCalculations/lagrangeInterpolation";
import {
  getProducedHeadForPumpAtFlowRateFt,
  getMaxHeadForPumpFt,
  getPressurePerStageHeadFt
} from "./curves";
import type { StageCalculations } from "./types";

/**
 * Calculate the maximum housing pressure rating based on the maximum head the pump can produce
 * This represents the pressure differential across the housing when operating at maximum head
 * Conversion: Pressure (PSIA) = Head (ft) * Liquid Density (lb/ft³) / 144.144
 */
export const getPressureFromHeadPSIA = (
  headFt: number,
  liquidDensityLBMFT3: number
): number => {
  if (headFt <= 0 || liquidDensityLBMFT3 <= 0) {
    return 0;
  }

  // Convert head to pressure: Pressure = Head * Density / 144.144
  return (headFt * liquidDensityLBMFT3) / 144.144;
};

export const getMaxHousingPressurePSIA = getPressureFromHeadPSIA;

/**
 * Unified head calculator for both primary (max) and taper (operating) pumps.
 * Pass flowRateBBLD to get operating head at that rate; omit it to get max head.
 */
export const getPumpHeadFt = (
  xrefPump: XrefPumps[number],
  gasHeadRerateRatio: number,
  frequencyRatio: number,
  pumpWearHeadCorrectionRatio: number,
  flowRateBBLD?: number
): number => {
  if (flowRateBBLD !== undefined) {
    return getProducedHeadForPumpAtFlowRateFt(
      flowRateBBLD,
      xrefPump,
      gasHeadRerateRatio,
      frequencyRatio,
      pumpWearHeadCorrectionRatio
    );
  }
  return getMaxHeadForPumpFt(
    xrefPump,
    gasHeadRerateRatio,
    frequencyRatio,
    pumpWearHeadCorrectionRatio
  );
};

export const getPowerPerStageBHP = (
  flowRateSTBD: number,
  xrefPump: XrefPumps[number],
  frequencyRatio: number,
  fluidDensitySpecificGravity: number,
  pumpWearBHPCorrectionRatio: number
): number => {
  return Math.max(
    0,
    getNominalPowerBrakeHP(
      flowRateSTBD,
      xrefPump.r_pump?.bhpb,
      xrefPump.r_pump?.bhp1,
      xrefPump.r_pump?.bhp2,
      xrefPump.r_pump?.bhp3,
      xrefPump.r_pump?.bhp4,
      xrefPump.r_pump?.bhp5
    ) *
      xrefPump.brake_horsepower_correction_ratio *
      frequencyRatio ** 3 *
      fluidDensitySpecificGravity *
      pumpWearBHPCorrectionRatio
  );
};

export const processPumpStages = (
  inletTemperatureF: number,
  inletPressurePSIA: number,
  xrefPump: XrefPumps[number],
  frequencyRatio: number,
  gasHeadRerateRatio: number,
  basicData: BasicData,
  fluidPropertiesContextIn: FluidPropertiesContext,
  correlations: Correlations,
  pumpWearBHPCorrectionRatio: number,
  pumpWearHeadCorrectionRatio: number,
  bubblePointPressurePSIAOverride?: number,
  dewPointPressurePSIAOverride?: number,
  condensateApi?: number,
  fluidViscosityCPOverride?: number | null,
  customFluidProperties?: LagrangeInterpolationMapWithGroupings | null,
  customViscosityProperties?: LagrangeInterpolationMapWithGroupings | null
): StageCalculations => {
  const pump = xrefPump.r_pump;

  if (!pump) {
    throw new TypeError();
  }

  const intakeFluidPropertiesContext = fluidPropertiesContextIn;

  // Legacy Reaver only used liquid rate when calculating produced/required TDH.
  // We are using fluid rate and fluid densities now.
  const intakeTotalFlowRateBBLD =
    intakeFluidPropertiesContext.fluidProperties?.fluidFlowRateBBLD || 0;
  const fluidDensitySpecificGravity = PoundsPerCubicFoot.toStandardUnit(
    intakeFluidPropertiesContext.fluidProperties?.fluidDensityLBMFT3 || 0
  );

  const liquidDensitySpecificGravity = PoundsPerCubicFoot.toStandardUnit(
    intakeFluidPropertiesContext.fluidProperties?.liquidDensityLBMFT3 || 0
  );

  const totalFlowRateCorrectedSTBD =
    (1 / xrefPump.rate_correction_ratio) *
    (intakeTotalFlowRateBBLD / frequencyRatio);

  // If flow rate is 0 or exceeds graphMax, head is 0
  const pumpGraphMax = pump.max_graph_rate_standard_pump_bpd || 0;
  const pumpIntakeFlowRateExceedsGraphMax =
    totalFlowRateCorrectedSTBD > pumpGraphMax;
  const invalidPumpIntakeFlowRate =
    pumpIntakeFlowRateExceedsGraphMax || totalFlowRateCorrectedSTBD <= 0;

  // Apply pump wear head correction
  // Get generated head for this stage only
  const generatedHeadFt = invalidPumpIntakeFlowRate
    ? 0
    : getPressurePerStageHeadFt(
        totalFlowRateCorrectedSTBD,
        xrefPump,
        gasHeadRerateRatio,
        frequencyRatio,
        pumpWearHeadCorrectionRatio
      );

  // If flow rate exceeds graphMax, use graphMax for bhp
  const flowRateForBHP = pumpIntakeFlowRateExceedsGraphMax
    ? pumpGraphMax
    : totalFlowRateCorrectedSTBD;
  // Apply pump wear bhp correction
  // Get generated bhp for this stage only
  const generatedPowerBrakeHP = getPowerPerStageBHP(
    flowRateForBHP,
    xrefPump,
    frequencyRatio,
    fluidDensitySpecificGravity,
    pumpWearBHPCorrectionRatio
  );

  // Calculate generated pressure up to this point by converting
  // generated head
  const generatedPressurePSIA =
    (generatedHeadFt / 144.144) *
    intakeFluidPropertiesContext.fluidProperties.fluidDensityLBMFT3;

  // Add incoming pressure to the generated pressure for discharge pressure
  // of this stage
  const dischargePressurePSIA = inletPressurePSIA + generatedPressurePSIA;

  // Calculate efficiency through all the stages up to this point
  const efficiency = getPumpEfficiencyRatio(
    intakeTotalFlowRateBBLD,
    generatedHeadFt,
    fluidDensitySpecificGravity,
    generatedPowerBrakeHP
  );

  // Calculate temperature through stages up to this point
  const temperatureDeltaF = getTemperatureDeltaForPumpStage(
    generatedHeadFt,
    intakeFluidPropertiesContext.fluidProperties.liquidSpecificHeatCapacity,
    efficiency
  );

  // Calculate discharge temperature of this stage
  const dischargeTemperatureF = inletTemperatureF + temperatureDeltaF;

  const dischargeFluidPropertiesContext = getFluidPropertiesContext(
    basicData,
    correlations,
    dischargePressurePSIA,
    dischargeTemperatureF,
    pump.housing_outer_diameter_inches,
    intakeFluidPropertiesContext.flowRatesAtSurface,
    intakeFluidPropertiesContext.flowRatesInSitu,
    intakeFluidPropertiesContext.flowRatePrerequisites,
    customFluidProperties,
    customViscosityProperties,
    bubblePointPressurePSIAOverride,
    dewPointPressurePSIAOverride,
    0,
    0,
    { fluidViscosityCPOverride },
    condensateApi,
    true
  );
  const averageFlowRateBBLD =
    ((intakeFluidPropertiesContext.flowRatesInSitu?.fluidFlowRateBBLD ?? 0) +
      (dischargeFluidPropertiesContext.flowRatesInSitu?.fluidFlowRateBBLD ??
        0)) /
    2;

  return {
    xrefPump,
    intakeFluidPropertiesContext,
    intakeTemperatureF: inletTemperatureF,
    intakePressurePSIA: inletPressurePSIA,
    dischargeFluidPropertiesContext,
    dischargeTemperatureF,
    dischargePressurePSIA,
    generatedHeadFt,
    generatedPressurePSIA,
    averageFlowRateBBLD,
    efficiency,
    generatedPowerBrakeHP,
    liquidDensitySpecificGravity,
    fluidDensitySpecificGravity,
    pumpIntakeFlowRateExceedsGraphMax
  };
};
